import apiService from './api';

export interface User {
  id: number;
  email: string;
  name: string;
}

export interface LoginSuccessResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  trustedDeviceToken?: string;
  requiresTwoFactor?: false;
}

export interface TwoFactorChallengeResponse {
  requiresTwoFactor: true;
  userId: number;
  method: 'app' | 'sms' | 'both';
  phoneNumber?: string;
}

export type LoginResponse = LoginSuccessResponse | TwoFactorChallengeResponse;

const TRUSTED_DEVICE_KEY = 'trustedDeviceToken';

class AuthService {
  async login(
    email: string,
    password: string,
    twoFactorCode?: string,
    twoFactorMethod?: string,
    rememberDevice?: boolean
  ): Promise<LoginResponse> {
    const payload: any = { email, password };

    if (twoFactorCode) payload.twoFactorCode = twoFactorCode;
    if (twoFactorMethod) payload.twoFactorMethod = twoFactorMethod;
    if (rememberDevice) payload.rememberDevice = true;

    // Send existing trusted device token if present
    const existingToken = localStorage.getItem(TRUSTED_DEVICE_KEY);
    if (existingToken) payload.trustedDeviceToken = existingToken;

    const response = await apiService.post('/auth/password/login', payload);

// If 2FA is required, clear stale trusted device token and return challenge
    if (response.data.requiresTwoFactor) {
      localStorage.removeItem(TRUSTED_DEVICE_KEY);
      return response.data as TwoFactorChallengeResponse;
    }

    // Normal login - store tokens
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }

    // Store trusted device token if returned
    if (response.data.trustedDeviceToken) {
      localStorage.setItem(TRUSTED_DEVICE_KEY, response.data.trustedDeviceToken);
    }

    return response.data as LoginSuccessResponse;
  }

  async register(data: any) {
    const response = await apiService.post('/auth/password/register', data);
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  }

  async requestMagicLink(email: string) {
    const response = await apiService.post('/auth/magic/request', { email });
    return response.data;
  }

  async verifyMagicLink(token: string) {
    const response = await apiService.post('/auth/magic/verify', { token });
    if (response.data.accessToken) {
      localStorage.setItem('accessToken', response.data.accessToken);
      localStorage.setItem('refreshToken', response.data.refreshToken);
    }
    return response.data;
  }

  logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // Note: intentionally keep trustedDeviceToken on logout
    // so returning users don't need to re-verify 2FA
  }

  clearTrustedDevice() {
    localStorage.removeItem(TRUSTED_DEVICE_KEY);
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem('accessToken');
  }

  getAccessToken(): string | null {
    return localStorage.getItem('accessToken');
  }
}

const authService = new AuthService();
export default authService;
